import {
  EmailComposerDraft,
  EmailMailboxId,
  IEmailMessage,
} from '@dataTypes/email.type'
import { formatEmailDateLabel } from '@constants/Message/emailDate.constants'
import axiosInstance from './axiosConfig'
import { getEnv } from '@utils/env'
import { isAxiosError } from 'axios'

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type EmailMessagesResponseData = {
  messages: IEmailMessage[]
}

export type EmailShowImages = 'ask' | 'always' | 'never'

export type EmailLabel = {
  id: string
  name: string
  color: string
}

export type EmailStarTag = {
  id: string
  name: string
  color: string
  sortOrder: number
}

type EmailLabelsResponseData = {
  labels: EmailLabel[]
}

type EmailStarTagsResponseData = {
  starTags: EmailStarTag[]
}

export type EmailFolder = {
  id: string
  name: string
}

type EmailFoldersResponseData = {
  folders: EmailFolder[]
}

export type SendEmailAttachment = {
  filename: string
  content: string
  type?: string
}

export type SendEmailRequest = {
  to: string[]
  cc?: string[]
  bcc?: string[]
  subject: string
  body: string
  replyTo?: string
  draftMessageId?: string
  attachments?: SendEmailAttachment[]
}

export type SendEmailResult = {
  messageId: string | null
  statusCode: number
}

type SendEmailResponseData = {
  email: SendEmailResult
}

type EmailMessageResponseData = {
  email: IEmailMessage
}

export type UpsertEmailDraftRequest = {
  draftMessageId?: string
  to?: string[]
  cc?: string[]
  bcc?: string[]
  subject: string
  body: string
  attachments?: { name: string; sizeLabel?: string; mimeType?: string }[]
}

const BUSINESS_LAYER_EMAIL = getEnv('REACT_APP_BUSINESS_LAYER_EMAIL', '')

export const isEmailBackendEnabled = (): boolean => true

const baseUrl = (): string => BUSINESS_LAYER_EMAIL.replace(/\/$/, '')

const withFormattedDateLabel = (message: IEmailMessage): IEmailMessage => ({
  ...message,
  dateLabel: formatEmailDateLabel(message.dateLabel),
})

export async function requestEmailMessages(): Promise<IEmailMessage[]> {
  const { data } = await axiosInstance.get<
    ApiResponse<EmailMessagesResponseData>
  >(`${baseUrl()}/api/v1/email`)

  const messages = data?.data?.messages
  if (!Array.isArray(messages)) {
    throw new Error(data?.message || 'Invalid email messages response')
  }

  return messages.map(withFormattedDateLabel)
}

export async function requestEmailLabels(): Promise<EmailLabel[]> {
  const { data } = await axiosInstance.get<
    ApiResponse<EmailLabelsResponseData>
  >(`${baseUrl()}/api/v1/email/label`)

  const labels = data?.data?.labels
  if (!Array.isArray(labels)) {
    throw new Error(data?.message || 'Invalid email labels response')
  }

  return labels
}

export async function requestEmailStarTags(): Promise<EmailStarTag[]> {
  const { data } = await axiosInstance.get<
    ApiResponse<EmailStarTagsResponseData>
  >(`${baseUrl()}/api/v1/email/star-tag`)

  const starTags = data?.data?.starTags
  if (!Array.isArray(starTags)) {
    throw new Error(data?.message || 'Invalid email star tags response')
  }

  return starTags
}

export async function requestCreateEmailStarTag(payload: {
  name: string
  color?: string
  sort_order?: number
}): Promise<EmailStarTag> {
  const { data } = await axiosInstance.post<
    ApiResponse<{ starTag: EmailStarTag }>
  >(`${baseUrl()}/api/v1/email/star-tag`, payload)
  const starTag = data?.data?.starTag
  if (!starTag) {
    throw new Error(data?.message || 'Invalid create star tag response')
  }
  return starTag
}

export async function requestUpdateEmailStarTag(
  emailStarTagId: string,
  patch: { name?: string; color?: string; sort_order?: number },
): Promise<EmailStarTag> {
  const { data } = await axiosInstance.put<
    ApiResponse<{ starTag: EmailStarTag }>
  >(`${baseUrl()}/api/v1/email/star-tag`, {
    email_star_tag_id: emailStarTagId,
    data: patch,
  })
  const starTag = data?.data?.starTag
  if (!starTag) {
    throw new Error(data?.message || 'Invalid update star tag response')
  }
  return starTag
}

export async function requestDeleteEmailStarTag(
  emailStarTagId: string,
): Promise<void> {
  await axiosInstance.delete(
    `${baseUrl()}/api/v1/email/star-tag/${emailStarTagId}`,
  )
}

export async function requestEmailFolders(): Promise<EmailFolder[]> {
  const { data } = await axiosInstance.get<
    ApiResponse<EmailFoldersResponseData>
  >(`${baseUrl()}/api/v1/email/folder`)

  const folders = data?.data?.folders
  if (!Array.isArray(folders)) {
    throw new Error(data?.message || 'Invalid email folders response')
  }

  return folders
}

export async function requestCreateEmailFolder(payload: {
  name: string
}): Promise<EmailFolder> {
  const { data } = await axiosInstance.post<
    ApiResponse<{ folder: EmailFolder }>
  >(`${baseUrl()}/api/v1/email/folder`, payload)
  const folder = data?.data?.folder
  if (!folder) {
    throw new Error(data?.message || 'Invalid create folder response')
  }
  return folder
}

export async function requestUpdateEmailFolder(
  emailFolderId: string,
  patch: { name?: string },
): Promise<EmailFolder> {
  const { data } = await axiosInstance.put<
    ApiResponse<{ folder: EmailFolder }>
  >(`${baseUrl()}/api/v1/email/folder`, {
    email_folder_id: emailFolderId,
    data: patch,
  })
  const folder = data?.data?.folder
  if (!folder) {
    throw new Error(data?.message || 'Invalid update folder response')
  }
  return folder
}

export async function requestDeleteEmailFolder(
  emailFolderId: string,
): Promise<void> {
  await axiosInstance.delete(
    `${baseUrl()}/api/v1/email/folder/${emailFolderId}`,
  )
}

export async function requestSendEmail(
  payload: SendEmailRequest,
): Promise<SendEmailResult> {
  const { data } = await axiosInstance.post<ApiResponse<SendEmailResponseData>>(
    `${baseUrl()}/api/v1/email/send`,
    payload,
  )

  const email = data?.data?.email
  if (!email) {
    throw new Error(data?.message || 'Invalid send email response')
  }

  return email
}

export type GoogleWorkspaceStatus = {
  googleAccountId: string
  clinicId: string
  googleEmail: string
  tokenExpiry: string | null
  historyId: string | null
  isConnected: boolean
}

type GoogleAuthUrlResponseData = {
  url: string
}

type GoogleStatusResponseData = {
  account: GoogleWorkspaceStatus | null
}

type GoogleConnectResponseData = {
  account: GoogleWorkspaceStatus
}

export type GoogleSyncResult = {
  synced: number
  historyId: string | null
}

export async function requestGoogleAuthUrl(): Promise<string> {
  const { data } = await axiosInstance.get<
    ApiResponse<GoogleAuthUrlResponseData>
  >(`${baseUrl()}/api/v1/email/google/auth-url`)

  const url = data?.data?.url
  if (!url) {
    throw new Error(
      data?.message || 'Invalid Google authorization URL response',
    )
  }
  return url
}

export async function requestGoogleWorkspaceStatus(): Promise<GoogleWorkspaceStatus | null> {
  const { data } = await axiosInstance.get<
    ApiResponse<GoogleStatusResponseData>
  >(`${baseUrl()}/api/v1/email/google/status`)
  return data?.data?.account ?? null
}

export async function requestConnectGoogleWorkspace(
  code: string,
): Promise<GoogleWorkspaceStatus> {
  const { data } = await axiosInstance.post<
    ApiResponse<GoogleConnectResponseData>
  >(`${baseUrl()}/api/v1/email/google/callback`, { code })

  const account = data?.data?.account
  if (!account) {
    throw new Error(data?.message || 'Invalid Google connect response')
  }
  return account
}

export async function requestSyncGoogleWorkspaceInbox(): Promise<GoogleSyncResult> {
  const { data } = await axiosInstance.post<ApiResponse<GoogleSyncResult>>(
    `${baseUrl()}/api/v1/email/google/sync`,
    {},
  )
  return data?.data ?? { synced: 0, historyId: null }
}

export async function requestDisconnectGoogleWorkspace(): Promise<void> {
  await axiosInstance.delete(`${baseUrl()}/api/v1/email/google/disconnect`)
}

export async function requestImportGoogleBackedEmailSettings(): Promise<void> {
  await axiosInstance.post(
    `${baseUrl()}/api/v1/email/google/import-settings`,
    {},
  )
}

export type EmailVacationSettings = {
  enabled: boolean
  subject: string
  message: string
  startTime: string | null
  endTime: string | null
}

type EmailVacationResponseData = {
  vacation: EmailVacationSettings | null
}

export async function requestEmailVacation(): Promise<EmailVacationSettings | null> {
  const { data } = await axiosInstance.get<
    ApiResponse<EmailVacationResponseData>
  >(`${baseUrl()}/api/v1/email/google/vacation`)
  return data?.data?.vacation ?? null
}

export async function requestUpdateEmailVacation(
  payload: EmailVacationSettings,
): Promise<EmailVacationSettings> {
  const { data } = await axiosInstance.put<
    ApiResponse<EmailVacationResponseData>
  >(`${baseUrl()}/api/v1/email/google/vacation`, payload)

  const vacation = data?.data?.vacation
  if (!vacation) {
    throw new Error(
      data?.message || 'Invalid update automatic replies response',
    )
  }
  return vacation
}

export type EmailSendAsSettings = {
  sendAsEmail: string
  displayName: string
  signature: string
  isDefault: boolean
}

type EmailSendAsResponseData = {
  sendAs: EmailSendAsSettings | null
}

export async function requestEmailSendAs(): Promise<EmailSendAsSettings | null> {
  const { data } = await axiosInstance.get<
    ApiResponse<EmailSendAsResponseData>
  >(`${baseUrl()}/api/v1/email/google/send-as`)
  return data?.data?.sendAs ?? null
}

export async function requestUpdateEmailSendAs(payload: {
  displayName: string
  signature: string
}): Promise<EmailSendAsSettings> {
  const { data } = await axiosInstance.put<
    ApiResponse<EmailSendAsResponseData>
  >(`${baseUrl()}/api/v1/email/google/send-as`, payload)

  const sendAs = data?.data?.sendAs
  if (!sendAs) {
    throw new Error(data?.message || 'Invalid update email signature response')
  }
  return sendAs
}

export type EmailSettingsData = {
  email_settings_id?: string
  clinic_id?: string
  display_name: string | null
  profile_picture_url: string | null
  grammar_suggestions?: boolean
  spelling_suggestions: boolean
  autocorrect_support: boolean
  notify_new_email: boolean
  notify_email_reply: boolean
  signature_enabled: boolean
  signature: string | null
  show_images: EmailShowImages
  show_unread_mail_indicator: boolean
  confirm_link_urls: boolean
}

type EmailSettingsResponseData = {
  email_settings: EmailSettingsData | null
}

export async function requestEmailSettings(): Promise<EmailSettingsData | null> {
  const { data } = await axiosInstance.get<
    ApiResponse<EmailSettingsResponseData>
  >(`${baseUrl()}/api/v1/email/settings`)
  return data?.data?.email_settings ?? null
}

export async function requestUpdateEmailSettings(
  payload: Partial<EmailSettingsData>,
): Promise<EmailSettingsData> {
  const { data } = await axiosInstance.put<
    ApiResponse<EmailSettingsResponseData>
  >(`${baseUrl()}/api/v1/email/settings`, payload)

  const settings = data?.data?.email_settings
  if (!settings) {
    throw new Error(data?.message || 'Invalid update email settings response')
  }
  return settings
}

export async function requestUpdateEmailMessage(
  emailMessageId: string,
  patch: Record<string, unknown>,
): Promise<IEmailMessage> {
  const { data } = await axiosInstance.put<
    ApiResponse<EmailMessageResponseData>
  >(`${baseUrl()}/api/v1/email`, {
    email_message_id: emailMessageId,
    data: patch,
  })
  const email = data?.data?.email
  if (!email) {
    throw new Error(data?.message || 'Invalid update email response')
  }
  return withFormattedDateLabel(email)
}

export async function requestBulkUpdateEmailMessages(
  ids: string[],
  patch: Record<string, unknown>,
): Promise<IEmailMessage[]> {
  const { data } = await axiosInstance.put<
    ApiResponse<EmailMessagesResponseData>
  >(`${baseUrl()}/api/v1/email/bulk`, { ids, data: patch })
  const messages = data?.data?.messages
  return Array.isArray(messages) ? messages.map(withFormattedDateLabel) : []
}

export async function requestBulkDeleteEmailMessages(
  ids: string[],
): Promise<void> {
  await axiosInstance.delete(`${baseUrl()}/api/v1/email/bulk`, {
    data: { ids },
  })
}

export async function requestSetEmailLabels(
  ids: string[],
  labelId: string,
  setChecked: boolean,
): Promise<void> {
  await axiosInstance.put(`${baseUrl()}/api/v1/email/labels`, {
    ids,
    labelId,
    setChecked,
  })
}

export async function requestClearEmailFolder(folderId: string): Promise<void> {
  await axiosInstance.put(`${baseUrl()}/api/v1/email/folder/clear`, {
    folderId,
  })
}

export async function requestUpsertEmailDraft(
  payload: UpsertEmailDraftRequest,
): Promise<IEmailMessage> {
  const { data } = await axiosInstance.post<
    ApiResponse<EmailMessageResponseData>
  >(`${baseUrl()}/api/v1/email/draft`, payload)
  const email = data?.data?.email
  if (!email) {
    throw new Error(data?.message || 'Invalid save draft response')
  }
  return withFormattedDateLabel(email)
}

export async function requestCreateEmailLabel(payload: {
  name: string
  color: string
}): Promise<EmailLabel> {
  const { data } = await axiosInstance.post<ApiResponse<{ label: EmailLabel }>>(
    `${baseUrl()}/api/v1/email/label`,
    payload,
  )
  const label = data?.data?.label
  if (!label) {
    throw new Error(data?.message || 'Invalid create label response')
  }
  return label
}

export async function requestUpdateEmailLabel(
  emailLabelId: string,
  patch: { name?: string; color?: string },
): Promise<EmailLabel> {
  const { data } = await axiosInstance.put<ApiResponse<{ label: EmailLabel }>>(
    `${baseUrl()}/api/v1/email/label`,
    { email_label_id: emailLabelId, data: patch },
  )
  const label = data?.data?.label
  if (!label) {
    throw new Error(data?.message || 'Invalid update label response')
  }
  return label
}

export async function requestDeleteEmailLabel(
  emailLabelId: string,
): Promise<void> {
  await axiosInstance.delete(`${baseUrl()}/api/v1/email/label/${emailLabelId}`)
}

function resolveEmailErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const fromBody = error.response?.data as { message?: string } | undefined
    if (fromBody?.message && typeof fromBody.message === 'string') {
      return fromBody.message
    }
    if (typeof error.message === 'string' && error.message) {
      return error.message
    }
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return fallback
}

export function getEmailMessagesErrorMessage(error: unknown): string {
  return resolveEmailErrorMessage(error, 'Failed to load emails')
}

export function getSendEmailErrorMessage(error: unknown): string {
  return resolveEmailErrorMessage(error, 'Failed to send email')
}

export function getEmailMutationErrorMessage(error: unknown): string {
  return resolveEmailErrorMessage(error, 'Failed to update email')
}

export function getGoogleWorkspaceErrorMessage(error: unknown): string {
  return resolveEmailErrorMessage(error, 'Failed to update Google connection')
}

export function getEmailSettingsErrorMessage(error: unknown): string {
  return resolveEmailErrorMessage(error, 'Could not save settings.')
}

export const mailboxToApiPatch = (
  mailbox: EmailMailboxId,
): Record<string, unknown> => ({
  mailbox,
  folder_id: null,
})

export const draftToApiPayload = (
  draft: EmailComposerDraft,
): UpsertEmailDraftRequest => ({
  draftMessageId: draft.draftMessageId,
  to: draft.to,
  cc: draft.cc,
  bcc: draft.bcc,
  subject: draft.subject,
  body: draft.body,
  attachments: draft.attachments?.map((attachment) => ({
    name: attachment.name,
    sizeLabel: attachment.sizeLabel,
    mimeType: attachment.mimeType,
  })),
})
